import { z } from 'zod';

export const ulid = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'Expected a ULID');

export const slug = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, numbers and single hyphens only');

export const AssetKind = z.enum(['image', 'video']);
export type AssetKind = z.infer<typeof AssetKind>;

/** Responsive image widths generated by the media pipeline. */
export const IMAGE_VARIANT_WIDTHS = [640, 1280, 2560] as const;

export const Asset = z.object({
  id: ulid,
  kind: AssetKind,
  /** Key of the uploaded original under `originals/` (never served publicly). */
  originalKey: z.string(),
  mime: z.string(),
  bytes: z.number().int().nonnegative(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  durationSec: z.number().positive().optional(),
  /** Public poster frame for videos. */
  posterKey: z.string().optional(),
  /** Public web-ready video file. */
  videoKey: z.string().optional(),
  /** Public image variants keyed by width in px. */
  variants: z.record(z.string(), z.string()).default({}),
  alt: z.string().max(500).default(''),
  tags: z.array(z.string().max(40)).default([]),
  status: z.enum(['uploading', 'processing', 'ready', 'failed']),
  /** Why processing failed, in words the admin can act on. */
  error: z.string().max(1000).optional(),
  processedAt: z.iso.datetime().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Asset = z.infer<typeof Asset>;

export const ItemMedia = z.object({
  assetId: ulid,
  caption: z.string().max(500).optional(),
});
export type ItemMedia = z.infer<typeof ItemMedia>;

export const Item = z.object({
  id: ulid,
  slug,
  title: z.string().min(1).max(200),
  year: z.number().int().min(1900).max(2100).optional(),
  medium: z.string().max(200).optional(),
  description: z.string().max(5000).optional(),
  /** Ordered media shown in the lightbox. The item "type" is derived from this. Drafts may be empty. */
  media: z.array(ItemMedia).max(100),
  /** Defaults to the first media entry when unset. */
  coverAssetId: ulid.optional(),
  order: z.number(),
  status: z.enum(['draft', 'published']),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Item = z.infer<typeof Item>;

export const Settings = z.object({
  heroVideoAssetId: ulid.optional(),
  heroPosterAssetId: ulid.optional(),
  heroTitle: z.string().max(120),
  heroSubtitle: z.string().max(200),
  contactEmail: z.email().optional(),
  social: z
    .array(z.object({ label: z.string().max(40), url: z.url() }))
    .max(10)
    .default([]),
});
export type Settings = z.infer<typeof Settings>;

/** Public, denormalised asset as embedded in the published snapshot. */
export const PublicAsset = z.object({
  id: ulid,
  kind: AssetKind,
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  alt: z.string(),
  /** Image variants: width (px) -> URL path. */
  srcset: z.record(z.string(), z.string()).optional(),
  video: z.string().optional(),
  poster: z.string().optional(),
  durationSec: z.number().optional(),
});
export type PublicAsset = z.infer<typeof PublicAsset>;

export const PublicItem = z.object({
  id: ulid,
  slug,
  title: z.string(),
  year: z.number().int().optional(),
  medium: z.string().optional(),
  description: z.string().optional(),
  cover: PublicAsset,
  media: z.array(PublicAsset.extend({ caption: z.string().optional() })).min(1),
});
export type PublicItem = z.infer<typeof PublicItem>;

/** Shape of `content/portfolio.json`, written on publish and read by the public site. */
export const PortfolioSnapshot = z.object({
  version: z.literal(1),
  publishedAt: z.iso.datetime(),
  settings: z.object({
    heroTitle: z.string(),
    heroSubtitle: z.string(),
    heroVideo: z.string().optional(),
    heroPoster: z.string().optional(),
    contactEmail: z.string().optional(),
    social: z.array(z.object({ label: z.string(), url: z.string() })),
  }),
  items: z.array(PublicItem),
});
export type PortfolioSnapshot = z.infer<typeof PortfolioSnapshot>;

export const ContactRequest = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.email().max(254),
  subject: z.string().trim().max(200).optional(),
  message: z.string().trim().min(1).max(3500),
  /** Honeypot: people leave it empty. Accepted when filled, so bots get no signal; the API drops it. */
  website: z.string().max(2000).optional(),
  /** Client timestamp (ms) when the form was rendered; used for a time-to-submit check. */
  renderedAt: z.number().int().positive(),
});
export type ContactRequest = z.infer<typeof ContactRequest>;

/* ------------------------------------------------------------------------------------------ */
/* Admin API inputs                                                                            */
/* ------------------------------------------------------------------------------------------ */

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => v || undefined)
    .optional();

/** Body for creating or replacing an item. Server fills id, order and timestamps. */
export const ItemInput = z
  .object({
    slug,
    title: z.string().trim().min(1).max(200),
    year: z.number().int().min(1900).max(2100).optional(),
    medium: optionalText(200),
    description: optionalText(5000),
    media: z.array(ItemMedia).max(100).default([]),
    coverAssetId: ulid.optional(),
    status: z.enum(['draft', 'published']).default('draft'),
  })
  .superRefine((item, ctx) => {
    if (item.status === 'published' && item.media.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['media'], message: 'A published item needs media' });
    }
    if (item.coverAssetId && !item.media.some((m) => m.assetId === item.coverAssetId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['coverAssetId'],
        message: 'The cover must be one of the item media',
      });
    }
    const ids = item.media.map((m) => m.assetId);
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({ code: 'custom', path: ['media'], message: 'Media must not repeat' });
    }
  });
export type ItemInput = z.infer<typeof ItemInput>;

export const ReorderRequest = z.object({ ids: z.array(ulid).min(1).max(1000) });
export type ReorderRequest = z.infer<typeof ReorderRequest>;

/** Accepted upload types. Videos must be web-ready MP4 (H.264/AAC, faststart). */
export const UPLOAD_LIMITS = {
  image: {
    mimes: ['image/jpeg', 'image/png', 'image/webp', 'image/avif'],
    maxBytes: 50 * 1024 ** 2,
  },
  video: { mimes: ['video/mp4'], maxBytes: 1024 ** 3 },
} as const;

export function kindForMime(mime: string): AssetKind | undefined {
  if ((UPLOAD_LIMITS.image.mimes as readonly string[]).includes(mime)) return 'image';
  if ((UPLOAD_LIMITS.video.mimes as readonly string[]).includes(mime)) return 'video';
  return undefined;
}

/** Body for requesting an upload. Dimensions are read client-side before uploading. */
export const AssetCreateRequest = z
  .object({
    filename: z
      .string()
      .trim()
      .min(1)
      .max(200)
      .regex(/^[^/\\]+$/, 'Filename must not contain path separators'),
    mime: z.string(),
    bytes: z.number().int().positive(),
    width: z.number().int().positive().max(20000),
    height: z.number().int().positive().max(20000),
    durationSec: z
      .number()
      .positive()
      .max(6 * 3600)
      .optional(),
    alt: z.string().max(500).optional(),
  })
  .superRefine((req, ctx) => {
    const kind = kindForMime(req.mime);
    if (!kind) {
      ctx.addIssue({
        code: 'custom',
        path: ['mime'],
        message: `Unsupported file type ${req.mime}`,
      });
      return;
    }
    if (req.bytes > UPLOAD_LIMITS[kind].maxBytes) {
      ctx.addIssue({ code: 'custom', path: ['bytes'], message: 'File is too large' });
    }
  });
export type AssetCreateRequest = z.infer<typeof AssetCreateRequest>;

export const AssetPatch = z.object({
  alt: z.string().max(500).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
});
export type AssetPatch = z.infer<typeof AssetPatch>;

export const SettingsInput = Settings;
export type SettingsInput = z.infer<typeof SettingsInput>;

export const DEFAULT_SETTINGS: Settings = {
  heroTitle: 'Marina',
  heroSubtitle: 'Visual Artist',
  social: [],
};

/** Presigned browser upload returned by `POST /api/admin/assets`. */
export type UploadTicket = {
  url: string;
  /** Form fields to send before the file in a multipart POST. */
  fields: Record<string, string>;
};
